// De maandredactie op het scherm, in de V2-vorm (owner-opname jfbgusr4f1nl).
//
//   samenvattingsblok  wat er deze maand uit de bronnen kwam, in een alinea
//   type-pills         skills · trends · subsidies · rapporten · bedrijven
//   de stroom          20-30 kaarten in de rangorde van de uitlichtrubriek
//   item-modal         relevant voor jou · beschrijving · advies · bronnen ·
//                      gespreksstarters
//   lege vormen        wat er deze maand niet was, mét de meting die dat zegt
//
// V2 IS HET SKELET, DE EDITIE-MOTOR IS DE INHOUD. De owner wees de V2-dummy aan
// ("de workspace-UX moet terug naar het V2-ontwerp, en de feed moet die layout
// ook krijgen") terwijl hij het ketenen zelf juist goed noemde. Wat hier
// verandert is dus de PRESENTATIE: dezelfde kaarten, dezelfde grond, dezelfde
// verificatie — een andere vorm eromheen.
//
// DE SOORT-AS VERVANGT DE VORM-AS ALS FILTER. Dertien vormen naast elkaar is
// redactionele machinerie; de owner noemde de V2-indeling per soort "een goede
// scheiding". De vorm blijft als badge op elke kaart staan, en `leeg` blijft
// zeggen welke vorm deze maand niets had.
//
// TWEE REGELS UIT EERDERE SESSIES ZITTEN HIER HARD IN. De telling in het menu
// verandert NIET als je erop klikt (W1c §7: een getal dat meebeweegt met je
// eigen filter is niet na te tellen). En een bak zonder materiaal verdwijnt
// niet maar toont zijn reden (W8: "geef duidelijk aan waarom het niet kan") —
// anders lijkt de editie compleet terwijl ze het niet is.

import { useMemo, useState } from "react";

import type { EditieKaartData, RedactieEditie } from "../contracts";
import { color, radius, size, weight } from "../tokens";
import { EditieKaart, KaartModal, type KaartOpenen } from "./EditieKaart";
import {
  GemetenPostKaart,
  GemetenPostModal,
  gemetenPosten,
  postenTelling,
  weefPosten,
  type GemetenPost,
} from "./GemetenPosten";
import type { PostenResponse } from "../contracts";
import type { PilId } from "../lib/kaartas";
import { bronbundels, PIL_VOLGORDE, PILLEN, pilVan } from "../lib/kaartas";
import type { VerwijzingKlik } from "./Verwijzing";

/** Eén subsidie-gebeurtenis uit de feed, zoals de workspace hem aanlevert. */
export type SubsidieItem = {
  id: string;
  naam: string;
  datum?: string | null;
  /** Het regeling-id (subject van het feed-item), voor de ontdubbeling met
   *  de vaste voorraad. */
  regelingId?: string | null;
};

/** Eén regeling uit het register — de vaste voorraad achter de subsidie-pil
 *  (B9, feedbackronde 27-08: "alle relevante subsidies, maar onderscheid wat
 *  beweegt en wat niet"). */
export type VasteRegeling = {
  id: string;
  naam: string;
  verstrekker?: string | null;
  status?: string | null;
};

/** "sluit over N dagen", verkleurend naarmate de deadline nadert (B9):
 *  amber vanaf 14 dagen, rood vanaf 7. Een datum in het verleden of te ver
 *  weg levert niets — de kaartdatum zelf staat er al. */
export function urgentie(datum?: string | null, vandaag?: Date): {
  label: string;
  kleur: "rood" | "amber" | "grijs";
} | null {
  if (!datum) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(datum);
  if (!m) return null;
  const nu = vandaag ?? new Date();
  // Kalenderdagen, geen klokrekenwerk: "sluit 30-08" is op de 27e "over 3
  // dagen", hoe laat het ook is.
  const doel = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const vandaagUtc = Date.UTC(nu.getFullYear(), nu.getMonth(), nu.getDate());
  const dagen = Math.round((doel - vandaagUtc) / 86_400_000);
  if (dagen < 0 || dagen > 90) return null;
  const label = dagen === 0 ? "vandaag" : dagen === 1 ? "over 1 dag" : `over ${dagen} dagen`;
  return { label, kleur: dagen <= 7 ? "rood" : dagen <= 14 ? "amber" : "grijs" };
}

/**
 * DE PIL-AS (owner-besluit 26-08): niet waar de kaart OVER gaat, maar WIE er
 * spreekt. Bij "Rapporten" spreekt de bron, bij "Synthese" spreken wij — en dat
 * verschil is precies wat een lezer moet kunnen zien voordat hij iets gelooft.
 *
 * Gemeten over de editie van augustus: de helft van de kaarten is onze eigen
 * analyse. Tot v1.6 zag zo'n kaart er identiek uit als een citaat uit een
 * OESO-rapport.
 *
 * De as zelf staat in src/lib/kaartas.ts, want de kaart, de stroom en de
 * preview lezen hem alle drie — één woordenlijst op één plek (backlog 30).
 */

function Pill({
  label,
  aantal,
  aan,
  titel,
  uit,
  onKlik,
}: {
  label: string;
  aantal: number | null;
  aan: boolean;
  titel?: string;
  uit?: boolean;
  onKlik: () => void;
}) {
  return (
    <button
      onClick={onKlik}
      title={titel}
      style={{
        background: aan ? color.ink : color.surface,
        color: aan ? color.surface : uit ? color.disabled : color.soft,
        border: `1px solid ${aan ? color.ink : color.border}`,
        borderRadius: radius.pill,
        padding: "5px 13px",
        fontSize: size.meta,
        fontWeight: weight.regular,
        fontFamily: "inherit",
        cursor: "pointer",
        transition: "all 0.12s",
      }}
    >
      {label}
      {/* Null = niet gemeten. Dat is iets anders dan nul en zegt dat ook. */}
      {aantal === null ? "" : ` ${aantal}`}
    </button>
  );
}

function Maandbeeld({
  editie,
  onOpen,
}: {
  editie: RedactieEditie;
  onOpen?: KaartOpenen;
}) {
  const blokken = editie.maandbeeld ?? [];
  if (blokken.length === 0) return null;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${Math.min(4, blokken.length)}, 1fr)`,
        gap: 1,
        background: color.border,
        border: `1px solid ${color.border}`,
        borderRadius: radius.card,
        overflow: "hidden",
        marginBottom: 16,
      }}
    >
      {blokken.map((b) => {
        const klikbaar = Boolean(onOpen && b.landing && Object.keys(b.landing).length);
        return (
          <div
            key={b.id}
            onClick={
              klikbaar
                ? () =>
                    onOpen?.({
                      soort: "tab",
                      ...(b.landing as Record<string, unknown>),
                    } as never)
                : undefined
            }
            style={{
              background: color.surface,
              padding: "13px 15px",
              cursor: klikbaar ? "pointer" : "default",
            }}
          >
            <div
              style={{
                fontSize: size.columnLabel,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: color.faint,
                marginBottom: 6,
              }}
            >
              {b.label}
            </div>
            <div
              style={{
                fontSize: typeof b.waarde === "number" ? 22 : size.meta,
                fontWeight: weight.bold,
                color: b.waarde == null ? color.faint : color.ink,
                lineHeight: 1.3,
              }}
            >
              {/* Null is een MELDING, geen nul: dit is niet gemeten. */}
              {b.waarde == null ? "niet gemeten" : String(b.waarde)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Het samenvattingsblok uit V2 (r. 3828: "Samenvatting · <periode>").
 *
 * De redactie schrijft hem zelf (redactie-v1.5), uit de koppen en zinnen die de
 * getallentoets en de verificatiepas al gehaald hebben. Hij heeft dus geen
 * eigen grond en kan per constructie niets beweren wat geen kaart al draagt.
 *
 * Een editie van een oudere generatie draagt hem niet, en dan staat de reden
 * er — een blok dat stilzwijgend leeg is leest als een gat.
 */
function Samenvatting({ editie }: { editie: RedactieEditie }) {
  const tekst = editie.samenvatting;
  return (
    <div
      style={{
        background: color.surface,
        border: `1px solid ${color.line}`,
        borderRadius: radius.card,
        padding: "18px 20px",
        marginBottom: 16,
      }}
    >
      <div
        style={{
          fontSize: size.label,
          fontWeight: weight.medium,
          color: color.faint,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          marginBottom: 9,
        }}
      >
        Samenvatting
      </div>
      {tekst ? (
        <p style={{ fontSize: size.meta, color: color.muted, lineHeight: 1.7, margin: 0 }}>
          {tekst}
        </p>
      ) : (
        <p style={{ fontSize: size.meta, color: color.soft, lineHeight: 1.7, margin: 0 }}>
          Deze editie komt uit een generatie die dit blok nog niet schreef. De
          redactie vat haar eigen editie samen uit de koppen en zinnen die de
          getallentoets en de verificatiepas al gehaald hebben; tot die generatie
          gedraaid is staat hier de reden en geen tekst die de meting niet draagt.
        </p>
      )}
    </div>
  );
}

function LegeVormen({ editie }: { editie: RedactieEditie }) {
  const leeg = editie.leeg ?? [];
  if (leeg.length === 0) return null;
  return (
    <div style={{ marginTop: 22 }}>
      <div
        style={{
          fontSize: size.columnLabel,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: color.faint,
          marginBottom: 9,
        }}
      >
        Wat er niet in staat
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {leeg.map((l) => (
          <div
            key={l.vorm}
            style={{
              border: `1px dashed ${color.border}`,
              borderRadius: radius.block,
              padding: "11px 14px",
              background: color.surfaceQuote,
            }}
          >
            <div style={{ fontSize: size.meta, color: color.body, fontWeight: weight.medium }}>
              {l.label ?? l.vorm}
            </div>
            <div
              style={{
                fontSize: size.meta,
                color: color.soft,
                marginTop: 4,
                lineHeight: 1.6,
              }}
            >
              {l.reden}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** De subsidiesignalen als rij, in dezelfde kaartvorm als de redactiekaarten. */
function SubsidieRij({
  item,
  onOpen,
}: {
  item: SubsidieItem;
  onOpen?: KaartOpenen;
}) {
  return (
    <div
      onClick={() => onOpen?.({ soort: "tab", tab: "funding" })}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen?.({ soort: "tab", tab: "funding" });
      }}
      style={{
        border: `1px solid ${color.line}`,
        borderRadius: radius.card,
        background: color.surface,
        padding: "15px 18px",
        marginBottom: 8,
        cursor: "pointer",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, gap: 8 }}>
        <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <span
            style={{
              fontSize: size.label,
              color: color.accentAmber,
              background: color.accentAmberBg,
              border: `1px solid ${color.accentAmber}33`,
              borderRadius: radius.pill,
              padding: "3px 9px",
            }}
          >
            Subsidiesignaal
          </span>
          {(() => {
            // B9: de aftellende urgentie — één deadline wordt vanzelf
            // dringender naarmate hij nadert, zonder duplicaat-rijen.
            const u = urgentie(item.datum);
            if (!u) return null;
            const kleur =
              u.kleur === "rood" ? color.accentRed
              : u.kleur === "amber" ? color.accentAmber
              : color.faint;
            return (
              <span
                style={{
                  fontSize: size.label,
                  color: kleur,
                  border: `1px solid ${kleur}44`,
                  borderRadius: radius.pill,
                  padding: "3px 9px",
                  fontWeight: weight.medium,
                }}
              >
                {u.label}
              </span>
            );
          })()}
        </span>
        {item.datum && (
          <span style={{ fontSize: size.meta, color: color.faint }}>{item.datum}</span>
        )}
      </div>
      <div
        style={{
          fontSize: size.body,
          fontWeight: weight.medium,
          color: color.ink,
          lineHeight: 1.45,
        }}
      >
        {item.naam}
      </div>
    </div>
  );
}

export function EditieStroom({
  editie,
  maanden,
  gekozenMaand,
  onKiesMaand,
  onOpen,
  onVerwijzing,
  subsidies = [],
  regelingen = [],
  posten = null,
}: {
  editie: RedactieEditie;
  maanden?: string[];
  gekozenMaand?: string;
  onKiesMaand?: (maand: string) => void;
  onOpen?: KaartOpenen;
  onVerwijzing?: VerwijzingKlik;
  /** Subsidiesignalen uit de feed (funding_alert), door de workspace geleverd. */
  subsidies?: readonly SubsidieItem[];
  /** De vaste voorraad: sector-relevante regelingen uit het register (B9).
   *  Alleen zichtbaar achter de subsidie-pil; in "Alles" telt alleen wat
   *  beweegt, anders duwt de voorraad elke maand hetzelfde blok in de stroom. */
  regelingen?: readonly VasteRegeling[];
  /** De gemeten feedposten (B6 golf 1): versnellers, cijferkaarten,
   *  vooruitblik, opkomende termen. Zij spreken met onze stem en wonen
   *  daarom onder de synthese-pil. */
  posten?: PostenResponse | null;
}) {
  const [soort, setSoort] = useState<PilId | null>(null);
  const [sortering, setSortering] = useState<
    "impact" | "gemengd" | "rangorde" | "bronnen"
  >("impact");
  const [open, setOpen] = useState<string | null>(null);

  const alleKaarten = editie.kaarten ?? [];

  // De tellingen worden ÉÉN KEER over de hele editie berekend en veranderen
  // dus niet als je op een pill klikt (W1c §7).
  const tellingen = useMemo(() => {
    const t: Record<string, number> = {};
    for (const k of alleKaarten) {
      const p = pilVan(k).id;
      t[p] = (t[p] ?? 0) + 1;
    }
    // B9: de pil telt wat er achter de pil aan RIJEN staat — de signalen
    // (beweegt) plus de vaste voorraad zónder de regelingen die al als
    // signaal bovenaan staan. Zo is het getal op de pil na te tellen in de
    // lijst eronder (W1c §7).
    const metEvent = new Set(
      subsidies.map((s) => s.regelingId).filter(Boolean),
    );
    t.subsidies =
      subsidies.length + regelingen.filter((r) => !metEvent.has(r.id)).length;
    // B6 golf 1: de gemeten posten spreken met onze stem en tellen dus mee
    // onder de synthese-pil — het getal blijft na te tellen in de lijst.
    t.synthese = (t.synthese ?? 0) + postenTelling(posten);
    return t;
  }, [alleKaarten, subsidies, regelingen, posten]);

  const gefilterd =
    soort === null
      ? alleKaarten
      : soort === "subsidies" || soort === "bedrijven"
        ? []
        : alleKaarten.filter((k) => pilVan(k).id === soort);

  /**
   * DE VOLGORDE, en waarom hij een keuze nodig had.
   *
   * De editie komt binnen op `positie`, en die volgt de onbegrensde
   * sorteerscore van de redactie-lane (bewijslagen wegen het zwaarst).
   *
   *   impact     de standaard (B5, feedbackronde 27-08): de redactie-orde mét
   *              een spreidingsregel — nooit meer dan twee kaarten van
   *              dezelfde pil op rij — en de synthesekaart altijd bovenaan.
   *              Zo dragen de eerste kaarten het meest en lees je toch geen
   *              zes ketens achter elkaar.
   *   gemengd    om de beurt uit elke pil: maximaal gespreid, de rangorde
   *              blijft binnen elke pil intact.
   *   rangorde   exact zoals de redactie hem schreef.
   *   bronnen    de best onderbouwde eerst.
   */
  const zichtbaar = useMemo(() => {
    if (sortering === "rangorde") return gefilterd;
    if (sortering === "bronnen") {
      return [...gefilterd].sort((a, b) => bronbundels(b).length - bronbundels(a).length);
    }
    if (sortering === "impact") {
      const rest = [...gefilterd];
      // De synthesekaart met de hoogste positie opent de stroom: dat is de
      // eigen stem van het platform, de etalage.
      const synthese = rest.findIndex((k) => pilVan(k).id === "synthese");
      if (synthese > 0) rest.unshift(rest.splice(synthese, 1)[0]!);
      const uit: EditieKaartData[] = [];
      while (rest.length) {
        const laatste = uit.slice(-2).map((k) => pilVan(k).id);
        const tweeZelfde = laatste.length === 2 && laatste[0] === laatste[1];
        let idx = 0;
        if (tweeZelfde) {
          const anders = rest.findIndex((k) => pilVan(k).id !== laatste[1]);
          if (anders !== -1) idx = anders;
        }
        uit.push(rest.splice(idx, 1)[0]!);
      }
      return uit;
    }
    const bakken = new Map<string, EditieKaartData[]>();
    for (const k of gefilterd) {
      const id = pilVan(k).id;
      const bak = bakken.get(id);
      if (bak) bak.push(k);
      else bakken.set(id, [k]);
    }
    const uit: EditieKaartData[] = [];
    for (let ronde = 0; uit.length < gefilterd.length; ronde += 1) {
      let gepakt = false;
      for (const id of PIL_VOLGORDE) {
        const kaart = bakken.get(id)?.[ronde];
        if (kaart) {
          uit.push(kaart);
          gepakt = true;
        }
      }
      if (!gepakt) break;
    }
    return uit;
  }, [gefilterd, sortering]);

  const zichtbareSubsidies =
    soort === null || soort === "subsidies" ? subsidies : [];

  // B9: de vaste voorraad zonder de regelingen die al als signaal bovenaan
  // staan — anders staat dezelfde regeling twee keer in de lijst.
  const voorraad = useMemo(() => {
    const metEvent = new Set(
      subsidies.map((s) => s.regelingId).filter(Boolean),
    );
    return regelingen.filter((r) => !metEvent.has(r.id));
  }, [regelingen, subsidies]);

  const postenZichtbaar = soort === null || soort === "synthese";
  const aantalPosten = postenTelling(posten);
  // Poetsronde 28-08: de posten staan niet meer als blok achter de stroom
  // maar geweven tussen de kaarten — gelijkmatig verdeeld, want ze dragen
  // geen redactionele rang. De klik opent eerst de onderbouwing (modal),
  // net als bij elke andere kaart.
  const [openPost, setOpenPost] = useState<GemetenPost | null>(null);
  const stroomItems = useMemo(
    () => weefPosten(zichtbaar, postenZichtbaar ? gemetenPosten(posten ?? null) : []),
    [zichtbaar, postenZichtbaar, posten],
  );

  const totaal = alleKaarten.length + subsidies.length + aantalPosten;
  const inBeeld =
    soort === "subsidies"
      ? zichtbareSubsidies.length + voorraad.length
      : zichtbaar.length + zichtbareSubsidies.length +
        (postenZichtbaar ? aantalPosten : 0);
  const kaartOpen = alleKaarten.find((k) => k.id === open) ?? null;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 12,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: size.meta, color: color.soft }}>
          {/* De kop telt wat er IN BEELD is; de pills tellen de hele editie.
              Dat verschil staat er expliciet bij zodra er gefilterd wordt. */}
          {soort === null
            ? `${totaal} inzichten in deze editie`
            : `${inBeeld} van de ${totaal} inzichten`}
          {editie.status === "concept" && (
            <span style={{ color: color.accentAmber }}> · concept, nog niet vastgezet</span>
          )}
        </div>
        {maanden && maanden.length > 1 && onKiesMaand && (
          <select
            value={gekozenMaand}
            onChange={(e) => onKiesMaand(e.target.value)}
            aria-label="Kies een maand"
            style={{
              fontSize: size.meta,
              color: color.body,
              border: `1px solid ${color.border}`,
              borderRadius: radius.input,
              padding: "5px 9px",
              fontFamily: "inherit",
              background: color.surface,
            }}
          >
            {maanden.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        )}
      </div>

      <Samenvatting editie={editie} />
      <Maandbeeld editie={editie} onOpen={onOpen} />

      {/* ── de pillen: wie er spreekt ─────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
        <Pill label="Alles" aantal={totaal} aan={soort === null} onKlik={() => setSoort(null)} />
        {PIL_VOLGORDE.map((id) => {
          const pil = PILLEN[id];
          const aantal = id === "bedrijven" ? null : (tellingen[id] ?? 0);
          return (
            <Pill
              key={id}
              label={pil.label}
              aantal={aantal}
              aan={soort === id}
              uit={id === "bedrijven"}
              titel={pil.reden ?? `${pil.label}: ${pil.spreker}.`}
              onKlik={() => setSoort(soort === id ? null : id)}
            />
          );
        })}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: size.label, color: color.faint }}>
          {soort ? `${PILLEN[soort].label}: ${PILLEN[soort].spreker}.` : "De pil zegt wie er spreekt."}
        </span>
        <label
          style={{ fontSize: size.label, color: color.faint, display: "flex", gap: 6, alignItems: "center" }}
        >
          Volgorde
          <select
            value={sortering}
            onChange={(e) =>
              setSortering(e.target.value as "impact" | "gemengd" | "rangorde" | "bronnen")
            }
            style={{
              border: `1px solid ${color.border}`,
              borderRadius: radius.pill,
              padding: "4px 10px",
              fontSize: 12,
              fontFamily: "inherit",
              color: color.body,
              background: color.surface,
              cursor: "pointer",
            }}
          >
            <option value="impact">Impact</option>
            <option value="gemengd">Gemengd</option>
            <option value="rangorde">Rangorde van de redactie</option>
            <option value="bronnen">Meeste bronnen eerst</option>
          </select>
        </label>
      </div>

      {stroomItems.map((item) =>
        item.soort === "kaart" ? (
          <EditieKaart
            key={item.kaart.id}
            kaart={item.kaart}
            onKlik={(kaart) =>
              // ELKE kaart opent de modal, ook de vraag-kaart (owner 26-08:
              // "er zijn feed items die direct doorklikken naar de chat, dat
              // is niet handig"). Je wilt eerst zien waar de vraag op rust;
              // de chat staat als knop in de modal, waar hij een keuze is in
              // plaats van een verrassing. De doorklik zelf blijft bestaan —
              // hij is alleen niet meer het gedrag van de kaart. Sinds de
              // poetsronde geldt dezelfde grammatica voor de gemeten posten.
              setOpen(kaart.id)
            }
            onVerwijzing={onVerwijzing}
          />
        ) : (
          <GemetenPostKaart key={item.post.sleutel} post={item.post} onDetail={setOpenPost} />
        ),
      )}
      {zichtbareSubsidies.map((s) => (
        <SubsidieRij key={s.id} item={s} onOpen={onOpen} />
      ))}
      {/* B9: achter de subsidie-pil staat óók de vaste voorraad — regelingen
          die gewoon open zijn, zonder naderend venster. "Het blijft altijd
          relevant" (owner); alleen telt hij niet mee in de gemengde stroom. */}
      {soort === "subsidies" && voorraad.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div
            style={{
              fontSize: size.label,
              fontWeight: weight.medium,
              color: color.faint,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            Doorlopend beschikbaar · {voorraad.length}
          </div>
          {voorraad.map((r) => (
            <div
              key={r.id}
              onClick={() => onOpen?.({ soort: "tab", tab: "funding" })}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") onOpen?.({ soort: "tab", tab: "funding" });
              }}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 10,
                border: `1px solid ${color.lineFaint}`,
                borderRadius: radius.card,
                background: color.surface,
                padding: "9px 14px",
                marginBottom: 6,
                cursor: "pointer",
              }}
            >
              <span
                style={{
                  fontSize: size.meta,
                  color: color.ink,
                  fontWeight: weight.medium,
                  minWidth: 0,
                }}
              >
                {r.naam}
              </span>
              <span style={{ fontSize: size.label, color: color.faint, flexShrink: 0 }}>
                {[r.verstrekker, r.status].filter(Boolean).join(" · ")}
              </span>
            </div>
          ))}
          <button
            onClick={() => onOpen?.({ soort: "tab", tab: "funding" })}
            style={{
              background: "none",
              border: "none",
              padding: "4px 2px",
              fontSize: size.meta,
              fontFamily: "inherit",
              color: color.soft,
              cursor: "pointer",
            }}
          >
            De feed toont subsidienieuws en de sector-relevante voorraad; alle
            regelingen, ook buiten de sector, staan op de Funding-tab →
          </button>
        </div>
      )}

      {/* Een lege bak verdwijnt niet maar zegt waarom hij leeg is. */}
      {inBeeld === 0 && soort !== null && (
        <div
          style={{
            border: `1px dashed ${color.border}`,
            borderRadius: radius.block,
            background: color.surfaceQuote,
            padding: "16px 18px",
            fontSize: size.meta,
            color: color.soft,
            lineHeight: 1.65,
          }}
        >
          {(soort && PILLEN[soort].reden) ||
            "Deze editie bevat geen kaart van deze soort. Dat is een uitkomst van de " +
              "rangorde, geen gebrek: de vormen die er wél zijn stonden hoger."}
        </div>
      )}

      <LegeVormen editie={editie} />

      {kaartOpen && (
        <KaartModal
          kaart={kaartOpen}
          rolLabel={editie.rolLabel}
          onSluit={() => setOpen(null)}
          onOpen={onOpen}
          onVerwijzing={onVerwijzing}
        />
      )}
      <GemetenPostModal post={openPost} onSluit={() => setOpenPost(null)} onOpen={onOpen} />
    </div>
  );
}
